import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import Modal from '../../../components/ui/Modal';
import { marketingContractService } from '../../../services/marketingContractService';
import { generateMarketingContractTemplate } from '../../../services/openaiContractTemplateService';
import { contractValidation } from '../../../utils/validationUtils';
import ContractPreviewModal from './ContractPreviewModal';

const MarketingContractModal = ({ isOpen, onClose, contractId, contractAmount, onSuccess }) => {
  const [formData, setFormData] = useState({
    arddjoRetainerPercentage: 5.0,
    ownerRetainerPercentage: 3.0,
    marketingDurationMonths: 6,
    exclusiveRights: true,
    marketingChannels: ['social_media', 'website'],
    targetPrice: contractAmount || 0,
    minimumPrice: contractAmount ? contractAmount * 0.9 : 0,
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calculatedAmounts, setCalculatedAmounts] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContract, setGeneratedContract] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showFullPreviewModal, setShowFullPreviewModal] = useState(false);

  const marketingChannelOptions = [
    { value: 'social_media', label: 'وسائل التواصل الاجتماعي' },
    { value: 'website', label: 'الموقع الإلكتروني' },
    { value: 'property_portals', label: 'بوابات العقارات' },
    { value: 'print_media', label: 'الإعلانات المطبوعة' },
    { value: 'email_marketing', label: 'التسويق عبر البريد' },
    { value: 'sms_marketing', label: 'التسويق عبر الرسائل' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // CLIENT-SIDE VALIDATION: Clear error for this field
    if (validationErrors?.[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
    
    // Recalculate amounts when percentages change
    if (field === 'arddjoRetainerPercentage' || field === 'ownerRetainerPercentage') {
      const amounts = marketingContractService?.calculateRetainerAmounts(
        contractAmount,
        field === 'arddjoRetainerPercentage' ? parseFloat(value) : formData?.arddjoRetainerPercentage,
        field === 'ownerRetainerPercentage' ? parseFloat(value) : formData?.ownerRetainerPercentage
      );
      setCalculatedAmounts(amounts);
    }
  };

  const handleBlur = (field) => {
    // Optional: Add any blur-specific validation logic here
    // For now, it's a placeholder for the onBlur event handlers
  };

  const handleChannelToggle = (channel) => {
    setFormData(prev => ({
      ...prev,
      marketingChannels: prev?.marketingChannels?.includes(channel)
        ? prev?.marketingChannels?.filter(c => c !== channel)
        : [...prev?.marketingChannels, channel]
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // CLIENT-SIDE VALIDATION: Validate before submitting
    const validation = contractValidation?.validateMarketingContract({
      ...formData,
      contractId,
      amount: contractAmount
    });
    
    if (!validation?.isValid) {
      setValidationErrors(validation?.errors);
      
      // Show error message
      const firstError = Object.values(validation?.errors)?.[0];
      alert(`خطأ في البيانات: ${firstError}`);
      return;
    }

    setIsSubmitting(true);

    try {
      await marketingContractService?.createMarketingContract({
        contractId,
        ...formData
      });

      alert('تم إنشاء عقد التسويق بنجاح');
      setValidationErrors({});
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error creating marketing contract:', error);
      
      // Handle specific error types
      let errorMessage = 'فشل في إنشاء عقد التسويق';
      
      if (error?.message?.includes('invalid input syntax for type uuid')) {
        errorMessage = 'خطأ في معرف العقد. يرجى إنشاء العقد الأساسي أولاً من خلال نموذج العقد الجديد.';
      } else if (error?.message?.includes('تجاوز حد استخدام')) {
        errorMessage = error?.message;
      } else if (error?.message) {
        errorMessage = error?.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateWithAI = async () => {
    setIsGenerating(true);
    try {
      const template = await generateMarketingContractTemplate({
        clientName: 'اسم العميل',
        propertyTitle: 'عنوان العقار',
        targetPrice: formData?.targetPrice,
        minimumPrice: formData?.minimumPrice,
        arddjoRetainerPercentage: formData?.arddjoRetainerPercentage,
        ownerRetainerPercentage: formData?.ownerRetainerPercentage,
        marketingDurationMonths: formData?.marketingDurationMonths,
        exclusiveRights: formData?.exclusiveRights,
        marketingChannels: formData?.marketingChannels
      });
      
      console.log('AI Generated Template:', template);
      
      // Store the generated contract and show preview
      setGeneratedContract(template);
      setShowPreview(true);
      
      alert('تم إنشاء قالب العقد بنجاح باستخدام الذكاء الاصطناعي. يمكنك الآن معاينة العقد وحفظه.');
    } catch (error) {
      console.error('Error generating template:', error);
      
      // Show user-friendly error message
      let errorMessage = 'فشل في إنشاء قالب العقد';
      
      if (error?.message?.includes('تجاوز حد استخدام')) {
        errorMessage = error?.message;
      } else if (error?.message?.includes('خطأ في مفتاح API')) {
        errorMessage = error?.message;
      } else if (error?.message?.includes('غير متاحة حالياً')) {
        errorMessage = error?.message;
      } else if (error?.message) {
        errorMessage = error?.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  React.useEffect(() => {
    if (contractAmount) {
      const amounts = marketingContractService?.calculateRetainerAmounts(
        contractAmount,
        formData?.arddjoRetainerPercentage,
        formData?.ownerRetainerPercentage
      );
      setCalculatedAmounts(amounts);
    }
  }, [contractAmount, formData?.arddjoRetainerPercentage, formData?.ownerRetainerPercentage]);

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="عقد تسويق عقاري" 
      size="lg"
      footer={null}
      icon={null}
      iconColor={null}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Retainer Percentages */}
        <div className="bg-muted rounded-lg p-4">
          <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Percent" size={16} />
            نسب العمولة المرنة
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                نسبة عمولة ARDDJO (%)
              </label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={formData?.arddjoRetainerPercentage}
                onChange={(e) => handleInputChange('arddjoRetainerPercentage', e?.target?.value)}
                onBlur={() => handleBlur('arddjoRetainerPercentage')}
                placeholder="2.5"
              />
              {calculatedAmounts && (
                <p className="text-xs text-muted-foreground mt-1">
                  المبلغ: {calculatedAmounts?.arddjoRetainerAmount?.toLocaleString('ar-JO')} دينار
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                نسبة عمولة المالك (%)
              </label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={formData?.ownerRetainerPercentage}
                onChange={(e) => handleInputChange('ownerRetainerPercentage', e?.target?.value)}
                onBlur={() => handleBlur('ownerRetainerPercentage')}
                placeholder="5.0"
              />
              {calculatedAmounts && (
                <p className="text-xs text-muted-foreground mt-1">
                  المبلغ: {calculatedAmounts?.ownerRetainerAmount?.toLocaleString('ar-JO')} دينار
                </p>
              )}
            </div>
          </div>
          {calculatedAmounts && (
            <div className="mt-4 p-3 bg-primary/10 rounded-lg">
              <p className="text-sm font-semibold text-primary">
                إجمالي العمولة: {calculatedAmounts?.totalRetainerAmount?.toLocaleString('ar-JO')} دينار
              </p>
            </div>
          )}
        </div>

        {/* Marketing Details */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              مدة التسويق (أشهر)
            </label>
            <Input
              type="number"
              min="1"
              max="24"
              value={formData?.marketingDurationMonths}
              onChange={(e) => handleInputChange('marketingDurationMonths', e?.target?.value)}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                السعر المستهدف (دينار)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData?.targetPrice}
                onChange={(e) => handleInputChange('targetPrice', e?.target?.value)}
                onBlur={() => handleBlur('targetPrice')}
                placeholder="150000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                الحد الأدنى للسعر (دينار)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData?.minimumPrice}
                onChange={(e) => handleInputChange('minimumPrice', e?.target?.value)}
                onBlur={() => handleBlur('minimumPrice')}
                placeholder="140000"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              الحقوق الحصرية
            </label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={formData?.exclusiveRights === true}
                  onChange={() => handleInputChange('exclusiveRights', true)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-foreground">نعم - حصري</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={formData?.exclusiveRights === false}
                  onChange={() => handleInputChange('exclusiveRights', false)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-foreground">لا - غير حصري</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              قنوات التسويق
            </label>
            <div className="grid grid-cols-2 gap-2">
              {marketingChannelOptions?.map(option => (
                <label
                  key={option?.value}
                  className="flex items-center gap-2 p-2 border border-border rounded-lg cursor-pointer hover:bg-muted transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={formData?.marketingChannels?.includes(option?.value)}
                    onChange={() => handleChannelToggle(option?.value)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-foreground">{option?.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              ملاحظات إضافية
            </label>
            <textarea
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="أدخل أي ملاحظات أو شروط خاصة..."
            />
          </div>
        </div>

        {/* Auto-Generate Contract Section */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start gap-3 mb-3">
            <Icon name="Sparkles" size={20} className="text-primary mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-foreground mb-1">
                إنشاء نموذج العقد تلقائياً
              </h4>
              <p className="text-xs text-muted-foreground">
                استخدم OpenAI لإنشاء نموذج عقد تسويق احترافي بناءً على البيانات المدخلة مع الحفاظ على تنسيق RTL العربي
              </p>
            </div>
          </div>
          <Button
            type="button"
            onClick={handleGenerateWithAI}
            disabled={isGenerating}
            className="w-full"
            variant="outline"
          >
            {isGenerating ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin" />
                جاري الإنشاء...
              </>
            ) : (
              <>
                <Icon name="FileText" size={16} />
                إنشاء نموذج العقد بالذكاء الاصطناعي
              </>
            )}
          </Button>
        </div>

        {/* Contract Preview */}
        {showPreview && generatedContract && (
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="Eye" size={16} />
                معاينة العقد المُنشأ
              </h4>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  onClick={() => setShowFullPreviewModal(true)}
                  variant="outline"
                  size="sm"
                >
                  <Icon name="Maximize2" size={14} className="ml-1" />
                  معاينة كاملة وطباعة
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  variant="ghost"
                  size="sm"
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
            </div>
            <div
              className="max-h-96 overflow-y-auto border border-border rounded-lg p-4 bg-background"
              dir="rtl"
              dangerouslySetInnerHTML={{ __html: generatedContract?.htmlContent }}
            />
            <div className="mt-3 text-xs text-muted-foreground">
              <p>تم الإنشاء بواسطة: {generatedContract?.metadata?.model}</p>
              <p>التاريخ: {new Date(generatedContract?.metadata?.generatedAt)?.toLocaleString('ar-JO')}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            إلغاء
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'جاري الإنشاء...' : 'إنشاء عقد التسويق'}
          </Button>
        </div>
      </form>
      {/* Full Preview Modal */}
      {generatedContract && (
        <ContractPreviewModal
          isOpen={showFullPreviewModal}
          onClose={() => setShowFullPreviewModal(false)}
          contract={{
            ...formData,
            generated_contract: generatedContract?.htmlContent,
            id: contractId,
            created_at: new Date()?.toISOString()
          }}
          contractType="marketing"
        />
      )}
    </Modal>
  );
};

export default MarketingContractModal;